/**
 * 스토리지 및 휴지통 관련 API
 */

import { apiCall } from "./base";
import type { BulkItemFailure } from "./library";

/** GET /api/storage/usage 응답 */
export interface StorageUsageResponse {
  used_bytes: number;
  quota_bytes: number;
}

export interface BulkMoveToTrashRequest {
  projectIds?: string[];
  filePaths?: string[];
}

export interface BulkMoveToTrashResponse {
  moved_ids: string[];
  failed: BulkItemFailure[];
}

/**
 * 스토리지 사용량·할당량 조회
 * used_bytes = library_objects의 size_bytes 합계, quota_bytes = users.vse_quota_bytes (기본 100GB)
 */
export const getStorageUsage = async (
  userToken: string | null
): Promise<StorageUsageResponse> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  return (await apiCall("/api/storage/usage", options)) as StorageUsageResponse;
};

/**
 * 영구 삭제 API (휴지통 파일 전용)
 * @param filePath - gcs 경로 (projectId 없을 때 사용)
 * @param userToken - 인증 토큰
 * @param projectId - viewr_projects.id (우선 사용)
 */
export const deleteFinalFilePermanently = async (
  filePath: string,
  userToken: string | null,
  projectId?: string
): Promise<unknown> => {
  const payload = projectId ? { project_id: projectId } : { file_path: filePath };
  const options: RequestInit = {
    method: "DELETE",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  try {
    return await apiCall("/api/storage/final-files/permanent", options);
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return await apiCall("/final-files/permanent", options);
    }
    throw err;
  }
};

/**
 * 샘플 파일 숨김 처리 API
 */
export const hideSampleFile = async (
  sampleName: string,
  fullPath: string | null,
  userToken: string | null
): Promise<unknown> => {
  const payload = {
    sample_name: sampleName,
    full_path: fullPath || null,
  };
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  try {
    return await apiCall("/api/storage/final-files/hide-sample", options);
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return await apiCall("/final-files/hide-sample", options);
    }
    throw err;
  }
};

/**
 * 샘플 파일 다시 표시 API
 */
export const showSampleFile = async (
  sampleName: string,
  userToken: string | null
): Promise<unknown> => {
  const payload = { sample_name: sampleName };
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  try {
    return await apiCall("/api/storage/final-files/show-sample", options);
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return await apiCall("/final-files/show-sample", options);
    }
    throw err;
  }
};

/**
 * 파일을 휴지통으로 이동
 * @param filePath - gcs 경로 (projectId 없을 때 사용)
 * @param userToken - 인증 토큰
 * @param projectId - viewr_projects.id (우선 사용)
 */
export const moveFileToTrash = async (
  filePath: string,
  userToken: string | null,
  projectId?: string
): Promise<unknown> => {
  const payload = projectId ? { project_id: projectId } : { file_path: filePath };
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  try {
    return await apiCall("/final-files/move-to-trash", options);
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return await apiCall("/api/storage/final-files/move-to-trash", options);
    }
    throw err;
  }
};

export const bulkMoveFinalFilesToTrash = async (
  userToken: string | null,
  request: BulkMoveToTrashRequest,
): Promise<BulkMoveToTrashResponse> => {
  const payload =
    request.projectIds && request.projectIds.length > 0
      ? { project_ids: request.projectIds }
      : { file_paths: request.filePaths ?? [] };
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  return (await apiCall(
    "/api/storage/final-files/bulk-move-to-trash",
    options,
  )) as BulkMoveToTrashResponse;
};

/**
 * 휴지통에서 파일 복원
 * @param filePath - gcs 경로 (projectId 없을 때 사용)
 * @param userToken - 인증 토큰
 * @param projectId - viewr_projects.id (우선 사용)
 */
export const restoreFileFromTrash = async (
  filePath: string,
  userToken: string | null,
  projectId?: string
): Promise<unknown> => {
  const payload = projectId ? { project_id: projectId } : { file_path: filePath };
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  try {
    return await apiCall("/final-files/restore-from-trash", options);
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return await apiCall(
        "/api/storage/final-files/restore-from-trash",
        options
      );
    }
    throw err;
  }
};

/**
 * 휴지통 파일 목록 조회
 */
export const listTrashFiles = async (
  userToken: string | null
): Promise<unknown> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  try {
    return await apiCall("/final-files/trash-list", options);
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return await apiCall("/api/storage/final-files/trash-list", options);
    }
    throw err;
  }
};

/**
 * 만료된 휴지통 파일 정리
 */
export const cleanupExpiredTrashFiles = async (
  userToken: string | null
): Promise<unknown> => {
  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  try {
    return await apiCall("/final-files/cleanup-expired-trash", options);
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return await apiCall(
        "/api/storage/final-files/cleanup-expired-trash",
        options
      );
    }
    throw err;
  }
};
